import {
  Grid,
  GridItem,
  Box,
  Heading,
  Text,
  Stack,
  Button,
  useToast,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import { useCallback, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import useRequest from 'src/utils/hooks/useRequest';
import { useDeepEffect } from 'src/utils/hooks';
import CheckoutModal from 'src/components/molecules/checkout-modal';
import { useFeatureFlagEnabled } from 'posthog-js/react';

export const FREE_TIER_POSITION = 1;
export const BASIC_TIER_POSITION = 2;
export const PRO_TIER_POSITION = 3;

export interface TierOption {
  name: string;
  position: number;
  description: string;
  price: string;
}

const tiers: TierOption[] = [
  {
    name: 'Free',
    position: FREE_TIER_POSITION,
    description: 'Free tier with limited features',
    price: '$0',
  },
  {
    name: 'Basic',
    position: BASIC_TIER_POSITION,
    description: 'Basic tier with all features',
    price: '$20/month',
  },
  {
    name: 'Pro',
    position: PRO_TIER_POSITION,
    description: 'Pro tier with all features',
    price: '$50/month',
  },
];

const TierOptions = (): JSX.Element => {
  const { marketplace, urlBase } = useMarketplaceContext();
  const router = useRouter();
  const currentTierPosition = marketplace?.tier?.position ?? 0;
  const toast = useToast();
  const { api } = useRequest();
  const [selectedTier, setSelectedTier] = useState<TierOption | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [sessionKey, setSessionKey] = useState<number>(0);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const isMarketplaceTiersEnabled = useFeatureFlagEnabled('marketplace_tiers');
  const { isOpen, onClose, onToggle } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const tryCancelTier = async () => {
    setIsCancelling(true);
    try {
      await cancelTier();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to cancel tier subscription. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const cancelTier = async () => {
    if (!marketplace) return;
    await api.marketplace.tier.cancel(marketplace.id);
    // clear the local cache of the marketplace
    const hostname = window.location.hostname || marketplace?.domain?.domain;
    localStorage.removeItem(`marketplace-${hostname}`);
    router.push('/admin/info?success=clear');
  };

  const changeTier = (tier: TierOption) => {
    if (tier.position === FREE_TIER_POSITION) {
      // trigger downgrade directly with confirm dialog
      onToggle();
    } else {
      setSelectedTier(tier);
      setShowModal(true);
    }
  };

  const fetchClientSecret = useCallback(() => {
    if (urlBase === '' || marketplace === null || selectedTier === null) {
      return Promise.resolve('');
    }

    return api.marketplace.tier.createCheckoutSession(marketplace.id, {
      tier_position: selectedTier.position,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlBase, marketplace?.id, selectedTier]);

  useDeepEffect(() => {
    const fetchCheckoutSession = async (marketplaceId: number, checkoutSessionId: string) => {
      const checkoutSession = await api.checkoutSession.get(marketplaceId, checkoutSessionId);
      if (checkoutSession.status === 'complete') {
        toast({
          title: 'Success',
          description:
            'Your payment was successfully processed! It may take up to a minute for the payment to process, try refreshing if the changes are not reflected immediately',
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
      }
    };

    if (router.isReady && router.query.checkout_session_id && marketplace) {
      const checkoutSessionId = router.query.checkout_session_id as string;
      fetchCheckoutSession(marketplace.id, checkoutSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query, marketplace]);

  return (
    <>
      {isMarketplaceTiersEnabled ? (
        <>
          {/* Stripe checkout modal */}
          {selectedTier && (
            <CheckoutModal
              key={sessionKey}
              isOpen={showModal}
              onClose={() => {
                setShowModal(false);
                setSessionKey(sessionKey + 1);
                setSelectedTier(null);
              }}
              fetchClientSecret={fetchClientSecret}
            />
          )}

          <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Revert to Free Tier
                </AlertDialogHeader>
                <AlertDialogBody>
                  Are you sure? Your tier subscription will remain active until the end of the
                  billing cycle.
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={onClose} isDisabled={isCancelling}>
                    No, Go back
                  </Button>
                  <Button
                    colorScheme="red"
                    onClick={tryCancelTier}
                    ml={3}
                    isLoading={isCancelling}
                    loadingText="Cancelling..."
                  >
                    Yes, Revert to Free Tier
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>

          <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)' }} gap={6}>
            {tiers.map((tier) => (
              <GridItem key={tier.name}>
                <Box
                  borderWidth="1px"
                  borderRadius="lg"
                  p={6}
                  h="100%"
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  alignItems="center"
                  textAlign="center"
                  backgroundColor={
                    selectedTier?.position === tier.position ? 'yellow.100' : 'white'
                  }
                >
                  <Stack spacing={3} w="100%" mb="3">
                    <Heading as="h3" size="md">
                      {tier.name}
                    </Heading>
                    <Text>{tier.description}</Text>
                    <Text fontWeight="bold" fontSize="2xl">
                      {tier.price}
                    </Text>
                  </Stack>
                  {!selectedTier && currentTierPosition > tier.position && (
                    <Button colorScheme="primary" mt="auto" onClick={() => changeTier(tier)}>
                      Downgrade
                    </Button>
                  )}
                  {!selectedTier && currentTierPosition === tier.position && (
                    <Button colorScheme="gray" disabled mt="auto">
                      Current Tier
                    </Button>
                  )}
                  {!selectedTier && currentTierPosition < tier.position && (
                    <Button colorScheme="primary" mt="auto" onClick={() => changeTier(tier)}>
                      Upgrade
                    </Button>
                  )}
                </Box>
              </GridItem>
            ))}
          </Grid>
        </>
      ) : (
        <div>Marketplace tiers are not yet available.</div>
      )}
    </>
  );
};

export default TierOptions;
